import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  ArrowBack as BackIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  Speed as SpeedIcon,
  Groups as TeamIcon,
} from '@mui/icons-material';
import { getUserData, removeUserData } from '../services/storageService';

const MarketingDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const userData = getUserData();
    if (!userData) {
      navigate('/login', { replace: true });
      return;
    }
    
    if (userData.role !== 'admin' && 
        (!userData.departmentRoles || !userData.departmentRoles.includes('marketing_dept'))) {
      navigate('/department-selection', { replace: true });
      return;
    }
    
    setUser(userData);
  }, [navigate]);

  const handleLogout = () => {
    removeUserData();
    navigate('/login', { replace: true });
  };

  if (!user) return null;

  const features = [
    {
      icon: TaskIcon,
      title: 'Quản lý Task',
      description: 'Tạo, giao và theo dõi task cho team Marketing',
      color: '#667eea',
    },
    {
      icon: SpeedIcon,
      title: 'Dashboard Realtime',
      description: 'Theo dõi tiến độ và hiệu suất làm việc',
      color: '#10b981',
    },
    {
      icon: TeamIcon,
      title: 'Làm việc nhóm',
      description: 'Phân công và cộng tác hiệu quả',
      color: '#f59e0b',
    },
    {
      icon: CheckIcon,
      title: 'Báo cáo tự động',
      description: 'Thống kê hiệu suất theo ngày/tuần',
      color: '#8b5cf6',
    },
  ];

  return (
    <Box
            sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
            }}
          >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
              }}
            />
      <Box
        sx={{
          position: 'absolute',
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }}
      />

      {/* Header */}
      <Box
        sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: { xs: 2, sm: 3 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/department-selection')}
          sx={{
            color: 'white',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          }}
          >
          Quay lại
        </Button>
          
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <Avatar
            sx={{ 
              width: 40,
              height: 40,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 600,
            }}
          >
            {user.fullName?.[0]}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: { borderRadius: 2, minWidth: 180 },
          }}
          >
          <MenuItem sx={{ py: 1.5 }}>
            <AccountIcon sx={{ mr: 1.5, color: '#667eea' }} />
            <Typography variant="body2" fontWeight={500}>
              {user.fullName}
            </Typography>
            </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: '#ef4444' }}>
            <LogoutIcon sx={{ mr: 1.5 }} />
              Đăng xuất
            </MenuItem>
          </Menu>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
      <Box
        sx={{
            textAlign: 'center',
            pt: { xs: 4, sm: 8 },
            pb: { xs: 4, sm: 6 },
          }}
        >
          <Typography
            variant="h3"
                  sx={{
              color: 'white',
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            Phòng Marketing
                        </Typography>
          <Typography
            variant="h6"
                          sx={{
              color: 'rgba(255,255,255,0.85)',
              fontWeight: 400,
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.15rem' },
                }}
              >
            Quản lý công việc hiệu quả với hệ thống Task Management hiện đại
                </Typography>
                
          {/* Main CTA Button */}
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowIcon />}
            onClick={() => navigate('/marketing-dashboard/tasks')}
                        sx={{
              bgcolor: 'white',
              color: '#667eea',
              px: { xs: 4, sm: 6 },
              py: 2,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              fontWeight: 700,
                        borderRadius: 3,
              textTransform: 'none',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              '&:hover': {
                bgcolor: 'white',
                transform: 'translateY(-3px)',
                boxShadow: '0 15px 50px rgba(0,0,0,0.25)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Vào Quản lý Task
          </Button>
                  </Box>

        {/* Features Grid */}
        <Grid container spacing={3} sx={{ pb: 6 }}>
          {features.map((feature, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.15)',
                    transform: 'translateY(-4px)',
                  },
                }}
                onClick={() => navigate('/marketing-dashboard/tasks')}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: feature.color,
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <feature.icon sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      mb: 1,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                      display: { xs: 'none', sm: 'block' },
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
          </Grid>

        {/* Footer hint */}
        <Box sx={{ textAlign: 'center', pb: 4 }}>
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.6)' }}
          >
            Nhấn vào bất kỳ đâu để bắt đầu quản lý task
          </Typography>
        </Box>
        </Container>
    </Box>
  );
};

export default MarketingDashboard; 
